/**
 * In-session state shared across the gameplay screens
 * (flash card → first pass → results → second chance → 3x-write → celebration).
 *
 * Each phase needs to know things the previous one figured out — the loaded
 * words and settings (FlashCard), Eva's first-pass attempts (FirstPass),
 * her second-chance attempts (SecondChance), and her 3x-write completions
 * (ThreeXWrite). Rather than passing payloads through navigation or
 * re-fetching per screen, the provider owns the in-flight session as a
 * single value.
 *
 * Surface as of Phase 7 slice 7:
 *   - startSession({weekId, words, settings, countsTowardProgress})
 *     — FlashCard kicks current-week play off, PastWeeks kicks past-week
 *       reviews off with countsTowardProgress=false.
 *   - recordFirstPassAttempt(wordEntryId, attempt) — FirstPass stores raw
 *     typed text per word (case-folding/trim happens at compare time via
 *     lib/spellingCompare).
 *   - recordSecondChanceAttempt(wordEntryId, attempt) — same shape; only
 *     populated for first-pass-missed words.
 *   - markThreeXComplete(wordEntryId) — sets the per-word 0..3 counter
 *     straight to 3. The UX is a single confirmation tap ("I wrote it
 *     three times"), not three separate "I did it" taps — Eva's already
 *     writing on paper, so making her tap thrice for the same word is
 *     just busywork. The 0..3 shape is preserved for spec §9 / the
 *     /api/sessions wire format.
 *   - clearSession() — explicit reset. Called on celebration sign-off
 *     and on "Start over" from the Resume? modal.
 *
 * Mid-session resume (spec §5.3): the provider hydrates from
 * localStorage on mount (10-min TTL) and persists on every change
 * that lands on a resumable phase. Celebration arrival + clearSession
 * both drop the snapshot.
 */
/* eslint-disable react-refresh/only-export-components --
 * Provider + hook + types co-located by convention. */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

import { useNavigation } from "@/context/NavigationContext";
import {
  clearSnapshot,
  loadSnapshot,
  RESUMABLE_PHASES,
  saveSnapshot,
} from "@/lib/sessionPersist";
import { setSoundMuted } from "@/lib/sounds";
import type { Settings, WordEntry } from "@/types/data";

export interface ActiveSession {
  weekId: number;
  words: WordEntry[];
  settings: Settings;
  /** ISO timestamp captured when the FlashCardScreen loaded — used as
   *  the session's start time for time-spent stats on the parent
   *  dashboard. Not exposed to Eva. */
  startedAt: string;
  /** Spec §5.4. `true` for current-week play (the default), `false`
   *  for past-week reviews — the latter persist as session rows for
   *  the dashboard but don't move the streak or unlock stickers. */
  countsTowardProgress: boolean;
  /** wordEntryId → exact typed text (untrimmed). */
  firstPassAttempts: Record<number, string>;
  /** wordEntryId → exact typed text (untrimmed). Populated only for
   *  first-pass-missed words. */
  secondChanceAttempts: Record<number, string>;
  /** wordEntryId → 0..3 "I did it" tap count. Populated only for
   *  first-pass-missed words. */
  threeXCompletions: Record<number, number>;
}

/** What the entry-point screen (FlashCard for Play Today, PastWeeks for
 *  reviews) hands to startSession — the persisted bits the rest of
 *  the phases add to. */
export type SessionSeed = Pick<
  ActiveSession,
  "weekId" | "words" | "settings" | "countsTowardProgress"
>;

interface SessionContextValue {
  session: ActiveSession | null;
  /** The phase to land on if Eva taps "Resume" on the home screen.
   *  Set from the hydrated snapshot on boot, and kept fresh as she
   *  navigates between gameplay screens. Null when there's nothing
   *  worth resuming (no session, or she's on the home / parent
   *  screens between sessions). */
  resumePhase: import("./NavigationContext").AppScreen | null;
  startSession: (seed: SessionSeed) => void;
  recordFirstPassAttempt: (wordEntryId: number, attempt: string) => void;
  recordSecondChanceAttempt: (wordEntryId: number, attempt: string) => void;
  markThreeXComplete: (wordEntryId: number) => void;
  clearSession: () => void;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  // Read snapshot once on mount. If valid (non-null + within TTL) the
  // session starts populated; HomeScreen will use resumePhase to offer
  // the Resume? modal on Play Today.
  const initial = loadSnapshot();
  const [session, setSession] = useState<ActiveSession | null>(initial?.session ?? null);
  const [resumePhase, setResumePhase] = useState<
    import("./NavigationContext").AppScreen | null
  >(initial?.phase ?? null);

  const { screen } = useNavigation();

  const startSession = useCallback((seed: SessionSeed) => {
    setSession({
      ...seed,
      // Stamp session start when FlashCard hands it off. The parent
      // dashboard reports time spent as (POST /api/sessions time - this).
      startedAt: new Date().toISOString(),
      firstPassAttempts: {},
      secondChanceAttempts: {},
      threeXCompletions: {},
    });
  }, []);

  const recordFirstPassAttempt = useCallback(
    (wordEntryId: number, attempt: string) => {
      setSession((s) => {
        if (s === null) return s;
        return {
          ...s,
          firstPassAttempts: { ...s.firstPassAttempts, [wordEntryId]: attempt },
        };
      });
    },
    [],
  );

  const recordSecondChanceAttempt = useCallback(
    (wordEntryId: number, attempt: string) => {
      setSession((s) => {
        if (s === null) return s;
        return {
          ...s,
          secondChanceAttempts: {
            ...s.secondChanceAttempts,
            [wordEntryId]: attempt,
          },
        };
      });
    },
    [],
  );

  const markThreeXComplete = useCallback((wordEntryId: number) => {
    setSession((s) => {
      if (s === null) return s;
      return {
        ...s,
        threeXCompletions: { ...s.threeXCompletions, [wordEntryId]: 3 },
      };
    });
  }, []);

  const clearSession = useCallback(() => {
    setSession(null);
    setResumePhase(null);
    clearSnapshot();
  }, []);

  // Keep the synth-sound mute flag in sync with parent settings. Lives
  // here (not the screen that loads settings) because every gameplay
  // screen reads `session.settings` and we want one source of truth.
  // ParentSettings's mute toggle also calls setSoundMuted directly so
  // the change is instant — this effect is the cross-session catch-up.
  useEffect(() => {
    if (session === null) return;
    setSoundMuted(!session.settings.audioEnabled);
  }, [session]);

  // Persistence side-effect. Runs on every (session, screen) change.
  // Only saves when we're on a gameplay phase — otherwise the
  // snapshot we'd write would be one Eva couldn't usefully resume to.
  // Also keeps `resumePhase` in sync with the latest gameplay screen
  // she touched, so on a refresh we always resume to the *latest*
  // beat, not the one she started on.
  useEffect(() => {
    if (session === null) return;
    if (!RESUMABLE_PHASES.has(screen)) return;
    saveSnapshot(session, screen);
    setResumePhase(screen);
  }, [session, screen]);

  const value = useMemo<SessionContextValue>(
    () => ({
      session,
      resumePhase,
      startSession,
      recordFirstPassAttempt,
      recordSecondChanceAttempt,
      markThreeXComplete,
      clearSession,
    }),
    [
      session,
      resumePhase,
      startSession,
      recordFirstPassAttempt,
      recordSecondChanceAttempt,
      markThreeXComplete,
      clearSession,
    ],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be called inside <SessionProvider>");
  return ctx;
}
